import { AsyncPipe, DatePipe } from '@angular/common';
import { ChangeDetectionStrategy, Component, computed, inject, input, OnInit } from '@angular/core';
import { RouterLink } from '@angular/router';
import { TranslateModule } from '@ngx-translate/core';
import { AppFeatureService } from '@nk/access-block';
import { COMPANY_API_SERVICE_TOKEN, GEO_API_SERVICE_TOKEN } from '@nk/api-lib';
import {
  CompanyCompositeEntity,
  CompanyDetailsEntity,
  CompanyLocationEntity,
  CompanyLocationTypeEntity,
  WithUuid,
} from '@nk/data-entities-lib';
import { arrayToDict, DateFormats, Dict, LOADING_STATE_HANDLER_SERVICE_TOKEN } from '@nk/utils-lib';
import { TooltipModule } from 'ngx-bootstrap/tooltip';
import { finalize, forkJoin, Observable } from 'rxjs';
import { first, map, switchMap } from 'rxjs/operators';

@Component({
  selector: 'nk-screening-report-company-base-info',
  imports: [AsyncPipe, DatePipe, RouterLink, TranslateModule, TooltipModule],
  templateUrl: './screening-report-company-base-info.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ScreeningReportCompanyBaseInfoComponent implements OnInit {
  public companyUuid = input<string>();
  public currentDate = new Date();
  public dateFormats = DateFormats;

  // Observable properties
  public companyDetails$!: Observable<CompanyDetailsEntity | null>;
  public companyBasicComposite$!: Observable<CompanyCompositeEntity | null>;
  public companyLocation$!: Observable<{
    companyLocationType: WithUuid<CompanyLocationTypeEntity>[];
    companyLocationByCompanyLocationType: Dict<CompanyLocationEntity>;
  } | null>;
  private _companyService = inject(COMPANY_API_SERVICE_TOKEN);
  private _reportLoadingStateService = inject(LOADING_STATE_HANDLER_SERVICE_TOKEN, { optional: true });
  private _companyDetailsLoadingId = computed(() => `${this.constructor.name}_companyDetails`);
  private _companyLocationLoadingId = computed(() => `${this.constructor.name}_companyLocation`);
  private _companyBasicCompositeLoadingId = computed(() => `${this.constructor.name}_companyBasicComposite`);
  private _geoService = inject(GEO_API_SERVICE_TOKEN);
  private _appFeatureService = inject(AppFeatureService);

  public ngOnInit(): void {
    if (this._reportLoadingStateService) {
      this._reportLoadingStateService.setLoadingState(this._companyDetailsLoadingId(), true);
      this._reportLoadingStateService.setLoadingState(this._companyLocationLoadingId(), true);
      this._reportLoadingStateService.setLoadingState(this._companyBasicCompositeLoadingId(), true);
    }
    const uuid = this.companyUuid();
    if (uuid) {
      this._loadCompanyData(uuid);
    }
  }

  private _loadCompanyData(companyUuid: string): void {
    // Load company details
    this.companyDetails$ = this._companyService.companyDetailsCache.get(companyUuid).pipe(
      map((data) => (data?.length ? data[0] : null)),
      finalize(() => {
        if (this._reportLoadingStateService) {
          this._reportLoadingStateService.setLoadingState(this._companyDetailsLoadingId(), false);
        }
      }),
    );

    // Load company location data
    this.companyLocation$ = forkJoin([
      this._geoService.companyLocationTypeCache.getStableCache().pipe(first()),
      this._geoService.companyLocationCache.get(companyUuid).pipe(first()),
    ]).pipe(
      map(([companyLocationType, companyLocation]) => ({
        companyLocationType,
        companyLocationByCompanyLocationType: arrayToDict(companyLocation, (a) => a.company_location_type_uuid),
      })),
      finalize(() => {
        if (this._reportLoadingStateService) {
          this._reportLoadingStateService.setLoadingState(this._companyLocationLoadingId(), false);
        }
      }),
    );

    // Load company composite data
    const hierarchyLevel = 5;
    this.companyBasicComposite$ = this._appFeatureService.activeAppFeatureDimsCache.get().pipe(
      switchMap((activeFeatures) => {
        const includeHistoricRolesFlag = Boolean(activeFeatures.flags.historical_roles);
        return this._companyService.companyCompositeCache.get(companyUuid, includeHistoricRolesFlag, hierarchyLevel);
      }),
      finalize(() => {
        if (this._reportLoadingStateService) {
          this._reportLoadingStateService.setLoadingState(this._companyBasicCompositeLoadingId(), false);
        }
      }),
    );
  }
}
